import { useMemo } from 'react'
import {
  useAllAccountFeatures,
  useAllAccountSummary,
  useCreditAccounts
} from 'const/syfApiQueries'
import {
  type SearchAction,
  type SearchItem,
  isSearchItemEligible
} from 'const/searchItems'
import { CARECREDIT } from 'const/account/clients'
import normalizeFeatureContext, {
  type AccountAggregate,
  type AccountContext
} from 'helpers/featureContext'

export interface EligibleAccount {
  accountId: string
  displayName: string
  accountLast4: string
  action?: SearchAction
}

export interface AccountWithContext extends EligibleAccount {
  context: AccountContext
}

export interface AccountContextsResult {
  accounts: AccountWithContext[]
  /** True until summaries and features resolve, so the UI waits instead of showing "no results". */
  isLoading: boolean
  isError: boolean
}

const useAccountContexts = (): AccountContextsResult => {
  const {
    data: { accounts = [] } = {},
    isLoading: isLoadingAccounts,
    isError
  } = useCreditAccounts()
  const summaries = useAllAccountSummary(accounts)
  const features = useAllAccountFeatures(accounts)

  const summaryVersions = useMemo(
    () => summaries.map((s) => s.dataUpdatedAt).join(),
    [summaries]
  )
  const featureVersions = useMemo(
    () => features.map((f) => f.dataUpdatedAt).join(),
    [features]
  )

  const isLoading =
    isLoadingAccounts ||
    summaries.some((summary) => summary.isLoading) ||
    features.some((feature) => feature.isLoading)

  const contexts = useMemo(
    () =>
      accounts
        .map((account, index) => ({
          account,
          summary: summaries[index],
          feature: features[index]
        }))
        .filter(({ summary }) => !summary?.isError && Boolean(summary?.data))
        .map(({ account, summary, feature }) => ({
          accountId: account.info['cipher.accountId'],
          displayName: account.product?.displayName || '',
          accountLast4: account.info?.accountLast4 || '',
          context: {
            account,
            summary: summary!.data!,
            features: feature?.data ?? null
          }
        })),
    // Query result objects are recreated every render, so key off resolved data instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts, summaryVersions, featureVersions]
  )

  return { accounts: contexts, isLoading, isError }
}

export default useAccountContexts

export const getAggregate = (contexts: AccountContext[]): AccountAggregate => ({
  careCreditAccountsCount: contexts.filter(
    ({ account }) => account.product?.portfolio?.toLowerCase() === CARECREDIT
  ).length
})

/** Accounts that can actually reach the selected feature. */
export const useSearchEligibleAccounts = (
  selectedItem: SearchItem | null
): EligibleAccount[] => {
  const { accounts } = useAccountContexts()

  return useMemo(() => {
    if (!selectedItem) return []
    const aggregate = getAggregate(accounts.map(({ context }) => context))
    return accounts.flatMap(({ context, ...account }) => {
      const ctx = normalizeFeatureContext(context, aggregate)
      if (!isSearchItemEligible(selectedItem, ctx)) return []
      return [{ ...account, action: selectedItem.getAction?.(ctx) }]
    })
  }, [accounts, selectedItem])
}
